import { DEFAULT_FONT, FONTS, type Font } from './fonts'
import { parseHex } from './theme'

export type LinkTarget = 'new' | 'top'

export const RADIUS_OPTIONS = [10, 25, 50, 100] as const

export type Radius = (typeof RADIUS_OPTIONS)[number]

export type Layout = 'both' | 'grid' | 'map'

export type View = 'grid' | 'map'

export type Corners = 'rounded' | 'square'

export type TagFilter = { name: string | null; prefix: string | null; hiddenTags: string[] }

export type EmbedConfig = {
  layout: Layout
  defaultView: View
  groupIds: number[]
  eventTypeIds: number[]
  tags: string[]
  color: string | null
  links: LinkTarget
  shifts: boolean
  radius: Radius
  showNearest: boolean
  dateFilter: boolean
  onlineFilter: boolean
  typeFilter: boolean
  tagFilters: TagFilter[]
  needsVolunteers: boolean
  showEventType: boolean
  showIntro: boolean
  pin: string | null
  corners: Corners
  font: Font
  heading: string | null
  placeholder: string | null
  cta: string | null
}

export const MAX_LIST_SIZE = 20

const MAX_HIDDEN_TAGS = 100

export const MAX_TAG_FILTERS = 3

export const NEW_TAG_FILTER: TagFilter = { name: null, prefix: null, hiddenTags: [] }

export const MAX_WORDING_LENGTH = { heading: 80, placeholder: 60, cta: 24, tagFilterName: 40, tagPrefix: 40 } as const

function list(raw: string, max = MAX_LIST_SIZE) {
  const items = raw
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)
  return Array.from(new Set(items)).slice(0, max)
}

function ids(raw: string) {
  const numeric = raw
    .split(',')
    .map((item) => item.trim())
    .filter((item) => /^\d+$/.test(item))
    .map(Number)
  return Array.from(new Set(numeric)).slice(0, MAX_LIST_SIZE)
}

const hex = (color: string) => color.replace('#', '')

const joined = (values: Array<string | number>) => values.join(',')

// Cut by code point, so a cap never splits an emoji in two.
const text = (maxLength: number) => (raw: string) => Array.from(raw.trim()).slice(0, maxLength).join('').trim() || null

const off = (raw: string) => (raw === '0' ? false : null)

type WriteContext = { config: EmbedConfig; defaultColor: string }

type Setting<T> = {
  param: string
  default: T
  read(raw: string): T | null
  write?(value: T, context: WriteContext): string | null
}

function supporterFilter(name: 'date' | 'online' | 'type', defaultOn: boolean): Setting<boolean> {
  return {
    param: 'filters',
    default: defaultOn,
    read: (raw) => raw.split(',').includes(name),
    write: (_, { config }) =>
      [config.dateFilter && 'date', config.onlineFilter && 'online', config.typeFilter && 'type']
        .filter(Boolean)
        .join(',') || 'none',
  }
}

function readTagFilters(raw: string): TagFilter[] | null {
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return null
  }
  if (!Array.isArray(parsed)) return null

  const filters = parsed
    .filter((item): item is Record<string, unknown> => typeof item === 'object' && item !== null)
    .slice(0, MAX_TAG_FILTERS)
    .map(({ name, prefix, hide }) => ({
      name: typeof name === 'string' ? text(MAX_WORDING_LENGTH.tagFilterName)(name) : null,
      prefix: typeof prefix === 'string' ? text(MAX_WORDING_LENGTH.tagPrefix)(prefix) : null,
      hiddenTags: Array.isArray(hide)
        ? list(hide.filter((tag): tag is string => typeof tag === 'string').join(','), MAX_HIDDEN_TAGS)
        : [],
    }))
  return filters.length > 0 ? filters : null
}

const writeTagFilters = (filters: TagFilter[]) =>
  JSON.stringify(
    filters.map(({ name, prefix, hiddenTags }) => ({
      ...(name && { name }),
      ...(prefix && { prefix }),
      ...(hiddenTags.length > 0 && { hide: hiddenTags }),
    }))
  )

const SETTINGS: { [K in keyof EmbedConfig]: Setting<EmbedConfig[K]> } = {
  layout: { param: 'layout', default: 'both', read: (raw) => (raw === 'grid' || raw === 'map' ? raw : null) },
  defaultView: {
    param: 'view',
    default: 'grid',
    read: (raw) => (raw === 'map' ? raw : null),
    write: (view, { config }) => (config.layout === 'both' ? view : null),
  },
  groupIds: { param: 'groups', default: [], read: ids, write: joined },
  eventTypeIds: { param: 'types', default: [], read: ids, write: joined },
  tags: { param: 'tags', default: [], read: list, write: joined },
  color: {
    param: 'color',
    default: null,
    read: parseHex,
    write: (color, { defaultColor }) =>
      color && color.toUpperCase() !== defaultColor.toUpperCase() ? hex(color) : null,
  },
  links: { param: 'links', default: 'new', read: (raw) => (raw === 'top' ? raw : null) },
  shifts: { param: 'shifts', default: true, read: off, write: () => '0' },
  radius: {
    param: 'radius',
    default: 25,
    read: (raw) => RADIUS_OPTIONS.find((option) => String(option) === raw) ?? null,
  },
  showNearest: { param: 'nearest', default: true, read: off, write: () => '0' },
  dateFilter: supporterFilter('date', true),
  onlineFilter: supporterFilter('online', true),
  typeFilter: supporterFilter('type', false),
  tagFilters: { param: 'tag_filters', default: [], read: readTagFilters, write: writeTagFilters },
  needsVolunteers: {
    param: 'needs',
    default: true,
    read: off,
    write: (_, { config }) => (config.shifts ? '0' : null),
  },
  showEventType: { param: 'type_label', default: false, read: (raw) => (raw === '1' ? true : null), write: () => '1' },
  showIntro: { param: 'intro', default: true, read: off, write: () => '0' },
  pin: {
    param: 'pin',
    default: null,
    read: parseHex,
    write: (pin, { config }) => (pin && config.layout !== 'grid' ? hex(pin) : null),
  },
  corners: { param: 'corners', default: 'rounded', read: (raw) => (raw === 'square' ? raw : null) },
  font: {
    param: 'font',
    default: DEFAULT_FONT,
    read: (raw) => FONTS.find(({ family }) => family === raw)?.family ?? null,
  },
  heading: { param: 'heading', default: null, read: text(MAX_WORDING_LENGTH.heading) },
  placeholder: { param: 'placeholder', default: null, read: text(MAX_WORDING_LENGTH.placeholder) },
  cta: { param: 'cta', default: null, read: text(MAX_WORDING_LENGTH.cta) },
}

type AnySetting = Setting<EmbedConfig[keyof EmbedConfig]>

const ROWS = Object.entries(SETTINGS) as Array<[keyof EmbedConfig, AnySetting]>

function configFrom(value: (setting: AnySetting) => EmbedConfig[keyof EmbedConfig]) {
  return Object.fromEntries(ROWS.map(([key, setting]) => [key, value(setting)])) as EmbedConfig
}

export const DEFAULT_EMBED_CONFIG: EmbedConfig = configFrom((setting) => setting.default)

export function decodeEmbedConfig(search: string): EmbedConfig {
  const params = new URLSearchParams(search)
  return configFrom((setting) => {
    const raw = params.get(setting.param)
    return (raw ? setting.read(raw) : null) ?? setting.default
  })
}

export function encodeEmbedConfig(config: EmbedConfig, { defaultColor }: { defaultColor: string }): string {
  const params = new URLSearchParams()
  ROWS.forEach(([key, setting]) => {
    const value = config[key]
    if (JSON.stringify(value) === JSON.stringify(setting.default)) return
    const raw = setting.write ? setting.write(value, { config, defaultColor }) : String(value)
    if (raw !== null) params.set(setting.param, raw)
  })
  return params.toString()
}

export function hasFilters(config: EmbedConfig) {
  return config.groupIds.length + config.eventTypeIds.length + config.tags.length > 0
}

export function withoutFilters(config: EmbedConfig): EmbedConfig {
  return { ...config, groupIds: [], eventTypeIds: [], tags: [] }
}

export function feedQuery(config: EmbedConfig): string {
  const params = new URLSearchParams({ per_page: '100' })
  config.groupIds.forEach((id) => params.append('group_ids[]', String(id)))
  config.eventTypeIds.forEach((id) => params.append('event_type_ids[]', String(id)))
  config.tags.forEach((tag) => params.append('tags[]', tag))
  return params.toString()
}
